/**
 * SCWT-PM-V2 · Phase 8 · Feb 2026 — Admin PM Marketing Resources.
 *
 * Catalog + per-asset enable/disable + version list + preview +
 * publish. Uses the admin-scoped endpoints — every call is gated by
 * `pm_marketing_resources_v2` visibility for the admin user.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, CheckCircle2, XCircle,
         RefreshCw, Eye, ToggleLeft, ToggleRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function AdminPmMarketingResources() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null);   // slug
  const [versions, setVersions] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [statusMsg, setStatusMsg] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pm-marketing-resources/catalog");
      setCatalog(data);
    } catch (e) {
      setStatusMsg({ error: e.response?.data?.detail || "Could not load catalog" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const loadVersions = async (slug) => {
    setSelected(slug); setVersions([]); setPreviewHtml("");
    try {
      const { data } = await api.get(`/admin/pm-marketing-resources/${slug}/versions`);
      setVersions(data.versions || []);
    } catch (e) {
      setStatusMsg({ error: e.response?.data?.detail || "Could not load versions" });
    }
  };

  const previewVersion = async (slug, vid) => {
    setPreviewHtml("");
    try {
      const url = `/admin/pm-marketing-resources/${slug}/preview.html`
        + (vid ? `?version_id=${vid}` : "");
      const r = await api.get(url, { responseType: "text" });
      setPreviewHtml(r.data);
    } catch (e) {
      setStatusMsg({ error: e.response?.data?.detail || "Preview failed" });
    }
  };

  const publish = async (slug, vid) => {
    try {
      await api.post(`/admin/pm-marketing-resources/${slug}/versions/${vid}/publish`);
      setStatusMsg({ ok: "Version published" });
      await loadVersions(slug);
      await refresh();
    } catch (e) {
      setStatusMsg({ error: e.response?.data?.detail || "Publish failed" });
    }
  };

  const toggleEnabled = async (slug, enabled) => {
    try {
      await api.post(`/admin/pm-marketing-resources/${slug}/settings`,
                     { enabled, reason: enabled ? "admin_enabled" : "admin_disabled" });
      await refresh();
    } catch (e) {
      setStatusMsg({ error: e.response?.data?.detail || "Toggle failed" });
    }
  };

  const partnerAssets = useMemo(
    () => (catalog?.assets || []).filter((a) => a.aud === "partner"),
    [catalog],
  );
  const pmAssets = useMemo(
    () => (catalog?.assets || []).filter((a) => a.aud === "pm"),
    [catalog],
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-700">
              <ArrowLeft className="w-3 h-3" /> Back to admin
            </Link>
            <h1 className="mt-1 text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" />
              PM &amp; PWS Partner Marketing Resources
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Phase 8 (Feb 2026). Flag <code className="text-[10px] bg-slate-100 px-1 rounded">pm_marketing_resources_v2</code>{" "}
              — nothing published or dispatched automatically.
            </p>
          </div>
          <button
            data-testid="pmm-refresh"
            onClick={refresh}
            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {statusMsg && (
          <div
            data-testid="pmm-status-msg"
            className={`mb-4 rounded-md border px-4 py-2 text-sm ${
              statusMsg.error ? "border-red-300 bg-red-50 text-red-800"
                              : "border-green-300 bg-green-50 text-green-800"
            }`}
          >
            {statusMsg.error || statusMsg.ok}
          </div>
        )}

        {catalog?.pricing?.status === "unavailable" && (
          <div
            data-testid="pmm-pricing-warning"
            className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 inline-flex items-start gap-2"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>
              <strong>LWH pricing is not currently available</strong> — the
              Water Health Program information sheet and any membership-
              specific asset will remain unavailable until pricing is
              configured.
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
            <div className="space-y-6">
              <CatalogSection title="PWS Partner resources"
                              assets={partnerAssets}
                              selected={selected}
                              onSelect={loadVersions}
                              onToggle={toggleEnabled} />
              <CatalogSection title="Property Manager resources"
                              assets={pmAssets}
                              selected={selected}
                              onSelect={loadVersions}
                              onToggle={toggleEnabled} />
            </div>

            <div className="space-y-6">
              {selected ? (
                <VersionPanel
                  slug={selected}
                  versions={versions}
                  onPreview={(vid) => previewVersion(selected, vid)}
                  onPublish={(vid) => publish(selected, vid)}
                />
              ) : (
                <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
                  Select an asset from the left to view its versions.
                </div>
              )}
              {previewHtml && (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Preview</div>
                  <iframe
                    data-testid="pmm-preview-iframe"
                    title="preview"
                    className="w-full min-h-[480px] border border-slate-100 rounded"
                    srcDoc={previewHtml}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function CatalogSection({ title, assets, selected, onSelect, onToggle }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">{title}</div>
      <ul className="divide-y divide-slate-100">
        {assets.map((a) => (
          <li
            key={a.slug}
            data-testid={`pmm-asset-row-${a.slug}`}
            className={`px-4 py-3 flex items-center justify-between gap-3 text-sm ${
              selected === a.slug ? "bg-cyan-50" : "hover:bg-slate-50"
            }`}
          >
            <button
              onClick={() => onSelect(a.slug)}
              data-testid={`pmm-open-${a.slug}`}
              className="text-left flex-1"
            >
              <div className="font-medium text-slate-800">{a.label}</div>
              <div className="text-[11px] text-slate-500">
                {a.slug}
                {a.requires_lwh_pricing && " · requires LWH pricing"}
                {a.available ? (
                  <span className="ml-2 text-green-700">Available</span>
                ) : (
                  <span className="ml-2 text-amber-700">
                    Unavailable: {a.unavailable_reason}
                  </span>
                )}
              </div>
            </button>
            <button
              data-testid={`pmm-toggle-${a.slug}`}
              onClick={(e) => { e.stopPropagation(); onToggle(a.slug, !a.enabled); }}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${
                a.enabled ? "text-green-800 bg-green-100" : "text-slate-600 bg-slate-100"
              }`}
              title={a.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
            >
              {a.enabled ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
              {a.enabled ? "Enabled" : "Disabled"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VersionPanel({ slug, versions, onPreview, onPublish }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-cyan-600" />
        Versions — <code className="text-[11px] bg-slate-100 px-1 rounded">{slug}</code>
      </div>
      {versions.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">No versions yet.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {versions.map((v) => (
            <li key={v.id} data-testid={`pmm-version-${v.id}`} className="px-4 py-3 text-sm flex items-center justify-between">
              <div>
                <div className="font-medium text-slate-800">
                  v{v.version_number} · <StatusBadge status={v.status} />
                </div>
                <div className="text-[11px] text-slate-500">
                  {v.change_summary || "no change summary"} · created {v.created_at?.slice(0,10)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`pmm-preview-${v.id}`}
                  onClick={() => onPreview(v.id)}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
                >
                  <Eye className="w-3 h-3" /> Preview
                </button>
                {v.status === "draft" && (
                  <button
                    data-testid={`pmm-publish-${v.id}`}
                    onClick={() => onPublish(v.id)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-cyan-600 text-white hover:bg-cyan-700"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Publish
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const styles = {
    draft:       "bg-slate-100 text-slate-700",
    published:   "bg-green-100 text-green-800",
    superseded:  "bg-amber-100 text-amber-800",
    rejected:    "bg-red-100 text-red-800",
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${styles[status] || "bg-slate-100"}`}>
      {status === "rejected" && <XCircle className="w-3 h-3 inline mr-0.5" />}
      {status}
    </span>
  );
}
